/**
 * AppShell — outer device frame + app window wrapper.
 *
 * H5 source: `.device-frame` (styles.css:423–441) + `.app-window` (styles.css:449–463)
 * Tablet/desktop: `@media (min-width: 768px)` (styles.css:6711–6726)
 *
 * Also mounts the global `<ToastHost />` so any module can fire
 * `useToastStore.getState().show(msg)` and get H5 `.toast` behaviour
 * (main.js:9907-9914) without touching App.tsx — keeping RULE 13
 * (no global chrome in App.tsx) satisfied.
 */

import { useEffect, useRef, type ReactNode } from "react";

import { ToastHost } from "@/shared/ui/Toast/index.js";

export interface AppShellProps {
  children: ReactNode;
  /** Accessible label for the app window landmark. */
  label?: string;
}

export function AppShell({ children, label = "树洞墙" }: AppShellProps) {
  const appWindowRef = useRef<HTMLElement>(null);

  // audit-2026-05-18 (UX-5.4): mirror H5 installKeyboardInsetListener
  // (main.js:2854). Without this, mobile keyboard pop-up doesn't update
  // `--keyboard-h`, so `.compose-form { padding-bottom: max(1rem,
  // var(--keyboard-h)) }` (styles.css:3791) can't push content above the
  // keyboard — symptom: scroll area / scrollbar appears compressed because
  // the form sits behind the keyboard. SSR-safe via `typeof window` guard.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    const root = appWindowRef.current;
    if (!vv || !root) return;
    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty("--keyboard-h", inset > 40 ? `${inset}px` : "0px");
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  return (
    <div className="device-frame">
      <main ref={appWindowRef} className="app-window" aria-label={label}>
        {children}
      </main>
      <ToastHost />
    </div>
  );
}
